import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Edit3,
    Loader2,
    Rocket,
    Trash2,
    X,
    Eye,
    Sparkles,
} from "lucide-react";
import { PartnerAPI } from "@/lib/partnerApi";
import { formatApiError } from "@/context/PartnerAuthContext";

const EDITABLE = ["name", "tagline", "description", "price", "image"];

export default function StoreEditor() {
    const [products, setProducts] = useState(null);
    const [edit, setEdit] = useState(null); // product being edited (preview-merged copy)
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(null);

    const load = async () => {
        try {
            const data = await PartnerAPI.catalog(true);
            setProducts(data.products);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        }
    };
    useEffect(() => {
        load();
    }, []);

    const openEdit = (p) => {
        setEdit({
            slug: p.slug,
            name: p.name || "",
            tagline: p.tagline || "",
            description: p.description || "",
            price: p.price ?? 0,
            image: p.image || "",
            _has_draft: p._has_draft,
        });
    };

    const saveDraft = async () => {
        if (!edit) return;
        setSaving(true);
        try {
            const patch = {};
            EDITABLE.forEach((f) => {
                patch[f] = f === "price" ? Number(edit[f]) : edit[f];
            });
            await PartnerAPI.saveProductDraft(edit.slug, patch);
            toast.success("Draft saved · not yet live");
            setEdit(null);
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const publish = async (slug) => {
        setPublishing(slug);
        try {
            await PartnerAPI.publishProduct(slug);
            toast.success("Published · 48h revert window open");
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setPublishing(null);
        }
    };

    const discard = async (slug) => {
        try {
            await PartnerAPI.discardProductDraft(slug);
            toast.success("Draft discarded");
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        }
    };

    if (products === null) {
        return (
            <div className="py-16 text-center text-cream/60">
                <Loader2 size={28} className="animate-spin inline text-gold" />
            </div>
        );
    }

    return (
        <div data-testid="partner-store-editor" className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-serif text-3xl text-cream">
                        Your Store
                    </h2>
                    <p className="text-cream/60 text-sm mt-1">
                        Edit any product. Drafts never go live until you
                        publish.
                    </p>
                </div>
                <a
                    href="/shop"
                    target="_blank"
                    rel="noreferrer"
                    className="btn-pill btn-ghost-light inline-flex text-xs"
                >
                    <Eye size={13} /> View live shop
                </a>
            </div>

            <div className="grid gap-3 mt-6">
                {products.map((p) => (
                    <article
                        key={p.slug}
                        data-testid={`partner-product-row-${p.slug}`}
                        className="bg-cream/5 border border-cream/10 rounded-sm p-5 flex gap-4 items-start flex-wrap md:flex-nowrap backdrop-blur"
                    >
                        <div className="w-16 h-16 rounded-sm overflow-hidden bg-cream-warm/20 shrink-0">
                            {p.image && (
                                <img
                                    src={p.image}
                                    alt=""
                                    className="w-full h-full object-cover"
                                />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-serif text-lg text-cream">
                                    {p.name}
                                </h3>
                                {p._live_edited && (
                                    <span className="text-[0.6rem] tracking-[0.25em] uppercase text-gold border border-gold/40 px-2 py-0.5 rounded-full">
                                        Edited · live
                                    </span>
                                )}
                                {p._has_draft && (
                                    <span
                                        data-testid={`partner-draft-badge-${p.slug}`}
                                        className="text-[0.6rem] tracking-[0.25em] uppercase text-forest bg-gold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                                    >
                                        <Sparkles size={10} /> Draft
                                    </span>
                                )}
                            </div>
                            <p className="text-cream/55 text-xs mt-1 font-mono">
                                {p.slug} · ${Number(p.price).toFixed(2)}
                            </p>
                            {p.tagline && (
                                <p className="text-cream/70 text-sm mt-2 line-clamp-2">
                                    {p.tagline}
                                </p>
                            )}
                        </div>
                        <div className="flex gap-2 shrink-0 ml-auto">
                            {p._has_draft && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => publish(p.slug)}
                                        disabled={publishing === p.slug}
                                        data-testid={`partner-publish-${p.slug}`}
                                        className="btn-pill btn-gold text-xs"
                                    >
                                        {publishing === p.slug ? (
                                            <Loader2
                                                size={12}
                                                className="animate-spin"
                                            />
                                        ) : (
                                            <>
                                                <Rocket size={12} /> Publish
                                            </>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => discard(p.slug)}
                                        data-testid={`partner-discard-${p.slug}`}
                                        className="btn-pill btn-ghost-light text-xs"
                                    >
                                        <Trash2 size={12} /> Discard
                                    </button>
                                </>
                            )}
                            <button
                                type="button"
                                onClick={() => openEdit(p)}
                                data-testid={`partner-edit-${p.slug}`}
                                className="btn-pill btn-ghost-light text-xs"
                            >
                                <Edit3 size={12} /> Edit
                            </button>
                        </div>
                    </article>
                ))}
            </div>

            {edit && (
                <div
                    className="fixed inset-0 z-50 bg-forest-deep/80 backdrop-blur flex items-center justify-center p-4"
                    data-testid="partner-edit-modal"
                    role="dialog"
                >
                    <div className="bg-cream text-forest rounded-sm max-w-xl w-full max-h-[90vh] overflow-y-auto p-6 md:p-8">
                        <div className="flex items-center justify-between">
                            <h3 className="font-serif text-2xl">
                                Edit · {edit.slug}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setEdit(null)}
                                data-testid="partner-edit-close"
                                className="p-1 hover:text-gold"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="mt-6 space-y-4">
                            <Field
                                label="Name"
                                value={edit.name}
                                onChange={(v) =>
                                    setEdit({ ...edit, name: v })
                                }
                                testid="partner-edit-name"
                            />
                            <Field
                                label="Tagline"
                                value={edit.tagline}
                                onChange={(v) =>
                                    setEdit({ ...edit, tagline: v })
                                }
                                testid="partner-edit-tagline"
                            />
                            <Field
                                label="Price (USD)"
                                type="number"
                                value={edit.price}
                                onChange={(v) =>
                                    setEdit({ ...edit, price: v })
                                }
                                testid="partner-edit-price"
                            />
                            <Field
                                label="Image URL"
                                value={edit.image}
                                onChange={(v) =>
                                    setEdit({ ...edit, image: v })
                                }
                                testid="partner-edit-image"
                            />
                            <Field
                                label="Description"
                                textarea
                                rows={5}
                                value={edit.description}
                                onChange={(v) =>
                                    setEdit({ ...edit, description: v })
                                }
                                testid="partner-edit-description"
                            />
                        </div>
                        <div className="mt-6 p-4 bg-cream-warm rounded-sm text-xs text-brown">
                            Saving creates a <strong>draft</strong>. The live
                            site is unchanged until you click Publish on the
                            product row.
                        </div>
                        <button
                            type="button"
                            onClick={saveDraft}
                            disabled={saving}
                            data-testid="partner-edit-save-draft"
                            className="btn-pill btn-primary w-full justify-center mt-6"
                        >
                            {saving ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                "Save Draft"
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, value, onChange, type = "text", textarea, rows = 3, testid }) {
    const common = {
        value: value ?? "",
        onChange: (e) => onChange(e.target.value),
        className:
            "mt-1.5 w-full px-4 py-3 bg-cream-warm border border-forest/15 rounded-sm text-forest focus:outline-none focus:border-gold",
        "data-testid": testid,
    };
    return (
        <label className="block">
            <span className="text-[0.65rem] tracking-[0.28em] uppercase text-brown">
                {label}
            </span>
            {textarea ? (
                <textarea rows={rows} {...common} />
            ) : (
                <input type={type} {...common} />
            )}
        </label>
    );
}
